/**
 * Склонение существительных по числу.
 * pluralize(5, 'часть', 'части', 'частей') → '5 частей'
 */
export function pluralize(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100
  const lastDigit = abs % 10
  if (abs > 10 && abs < 20) return `${n} ${many}`
  if (lastDigit > 1 && lastDigit < 5) return `${n} ${few}`
  if (lastDigit === 1) return `${n} ${one}`
  return `${n} ${many}`
}

/**
 * Маппинг level → формы склонения [one, few, many]
 */
const LEVEL_FORMS: Record<string, [string, string, string]> = {
  part: ['часть', 'части', 'частей'],
  section: ['раздел', 'раздела', 'разделов'],
  subsection: ['подраздел', 'подраздела', 'подразделов'],
  chapter: ['глава', 'главы', 'глав'],
  article: ['статья', 'статьи', 'статей'],
  point: ['пункт', 'пункта', 'пунктов'],
  subpoint: ['подпункт', 'подпункта', 'подпунктов'],
  subsubpoint: ['подподпункт', 'подподпункта', 'подподпунктов'],
}

const DEFAULT_FORMS: [string, string, string] = ['элемент', 'элемента', 'элементов']

/**
 * Склонение названия уровня иерархии по числу.
 * pluralizeLevel(3, 'chapter') → '3 главы'
 */
export function pluralizeLevel(n: number, level: string): string {
  const forms = LEVEL_FORMS[level] || DEFAULT_FORMS
  return pluralize(n, ...forms)
}

/**
 * Маппинг level → именительный падеж единственного числа (с заглавной буквы)
 */
const LEVEL_LABELS: Record<string, string> = {
  part: 'Часть',
  section: 'Раздел',
  subsection: 'Подраздел',
  chapter: 'Глава',
  article: 'Статья',
  point: 'Пункт',
  subpoint: 'Подпункт',
  subsubpoint: 'Подподпункт',
}

/**
 * Формирует заголовок узла с указанием типа и номера.
 * formatNodeTitle('article', '1', 'Уголовное законодательство') → 'Статья 1. Уголовное законодательство'
 * formatNodeTitle('chapter', '2', 'ЗАКЛЮЧЕНИЕ БРАКА') → 'Глава 2. ЗАКЛЮЧЕНИЕ БРАКА'
 * Если num отсутствует — возвращает просто title.
 * Если title уже начинается с label (напр. "ЧАСТЬ ВТОРАЯ") — не дублирует.
 */
export function formatNodeTitle(level: string, num: string | undefined | null, title: string): string {
  if (!num) return title
  const label = LEVEL_LABELS[level]
  if (!label) return title

  // Проверяем, не начинается ли title уже с этого label (case-insensitive)
  const titleUpper = title.toUpperCase()
  const labelUpper = label.toUpperCase()
  if (titleUpper.startsWith(labelUpper)) return title

  return `${label} ${num}. ${title}`
}

/**
 * Получить label следующего уровня иерархии.
 * Принимает текущий level и hierarchy массив из law_type.
 * getChildLevelLabel('part', hierarchy) → 'section'
 */
export function getChildLevel(
  currentLevel: string,
  hierarchy: { level: string; label: string }[]
): string | null {
  const idx = hierarchy.findIndex(h => h.level === currentLevel)
  if (idx >= 0 && idx + 1 < hierarchy.length) {
    return hierarchy[idx + 1].level
  }
  return null
}
